/**
 * Path functionality.
 * @module path
 * @license MIT
 *
 * @copyright 2011 Alexis Deveria, 2011 Jeff Schiller
 */

import { getTransformList } from './svgtransformlist';
import { shortFloat } from '../common/units';
import { transformPoint } from './math';
import {
    getRotationAngle, getBBox,
    getRefElem, findDefs, isNullish,
    getBBox as utilsGetBBox
} from './utilities';
import {
    init as pathMethodInit, insertItemBeforeMethod, ptObjToArrMethod, getGripPtMethod,
    getPointFromGripMethod, addPointGripMethod, getGripContainerMethod, addCtrlGripMethod,
    getCtrlLineMethod, getPointGripMethod, getControlPointsMethod, replacePathSegMethod,
    getSegSelectorMethod, Path
} from './path-method';
import {
    init as pathActionsInit, pathActionsMethod
} from './path-actions';
import { SelectorManager } from './select';
import { Point } from 'canvg';
import { Command } from './undocommands';
import SvgCanvas from './svgcanvas';

const segData: { [key: number]: string[] } = {
    2: ['x', 'y'], // PATHSEG_MOVETO_ABS
    3: ['x', 'y'], // PATHSEG_MOVETO_REL
    4: ['x', 'y'], // PATHSEG_LINETO_ABS
    5: ['x', 'y'], // PATHSEG_LINETO_REL
    6: ['x', 'y', 'x1', 'y1', 'x2', 'y2'], // PATHSEG_CURVETO_CUBIC_ABS
    7: ['x', 'y', 'x1', 'y1', 'x2', 'y2'], // PATHSEG_CURVETO_CUBIC_REL
    8: ['x', 'y', 'x1', 'y1'], // PATHSEG_CURVETO_QUADRATIC_ABS
    9: ['x', 'y', 'x1', 'y1'], // PATHSEG_CURVETO_QUADRATIC_REL
    10: ['x', 'y', 'r1', 'r2', 'angle', 'largeArcFlag', 'sweepFlag'], // PATHSEG_ARC_ABS
    11: ['x', 'y', 'r1', 'r2', 'angle', 'largeArcFlag', 'sweepFlag'], // PATHSEG_ARC_REL
    12: ['x'], // PATHSEG_LINETO_HORIZONTAL_ABS
    13: ['x'], // PATHSEG_LINETO_HORIZONTAL_REL
    14: ['y'], // PATHSEG_LINETO_VERTICAL_ABS
    15: ['y'], // PATHSEG_LINETO_VERTICAL_REL
    16: ['x', 'y', 'x2', 'y2'], // PATHSEG_CURVETO_CUBIC_SMOOTH_ABS
    17: ['x', 'y', 'x2', 'y2'], // PATHSEG_CURVETO_CUBIC_SMOOTH_REL
    18: ['x', 'y'], // PATHSEG_CURVETO_QUADRATIC_SMOOTH_ABS
    19: ['x', 'y'], // PATHSEG_CURVETO_QUADRATIC_SMOOTH_REL
};

/**
 * @tutorial LocaleDocs
 * @typedef {module:locale.LocaleStrings|PlainObject} module:path.uiStrings
 * @property {PlainObject<string, string>} ui
*/
const uiStrings = {};

/**
* @function module:path.setUiStrings
* @param {module:path.uiStrings} strs
* @returns {void}
*/
export const setUiStrings = function (strs) {
    Object.assign(uiStrings, strs.ui);
};

let pathFuncs: [0, ...string[]] = [0];

let linkControlPts = true;

// Stores references to paths via IDs.
// TODO: Make this cross-document happy.
let pathData = {};

/**
* @function module:path.setLinkControlPoints
* @param {boolean} lcp
* @returns {void}
*/
export const setLinkControlPoints = function (lcp: boolean) {
    linkControlPts = lcp;
};

export const getLinkControlPoints = function () {
    return linkControlPts;
};

/**
 * @name module:path.path
 * @type {null|module:path.Path}
 * @memberof module:path
*/
export let path: Path = null;

let editorContext_: EditorContext = null;
let svgCanvas: SvgCanvas = null;

/**
* @external MouseEvent
*/

/**
* Object with the following keys/values.
* @typedef {PlainObject} module:path.SVGElementJSON
* @property {string} element - Tag name of the SVG element to create
* @property {PlainObject<string, string>} attr - Has key-value attributes to assign to the new element. An `id` should be set so that {@link module:utilities.EditorContext#addSVGElementFromJson} can later re-identify the element for modification or replacement.
* @property {boolean} [curStyles=false] - Indicates whether current style attributes should be applied first
* @property {module:path.SVGElementJSON[]} [children] - Data objects to be added recursively as children
* @property {string} [namespace="http://www.w3.org/2000/svg"] - Indicate a (non-SVG) namespace
*/
export interface SVGElementJSON {
    element: string;
    attr: { [key: string]: string | number };
    curStyles?: boolean;
    children?: SVGElementJSON[];
    namespace?: string;
}

/**
 * @interface module:path.EditorContext
 * @property {module:select.SelectorManager} selectorManager
 * @property {module:svgcanvas.SvgCanvas} canvas
 */
export interface EditorContext {
    /**
     * @function module:path.EditorContext#resetD
     * @param {SVGPathElement} p
     * @returns {void}
    */
    resetD(p: SVGPathElement);

    /**
     * @function module:path.EditorContext#remapElement
     * @param {Element} selected - DOM element to be changed
     * @param {PlainObject<string, string>} changes - Object with changes to be remapped
     * @param {SVGMatrix} m - Matrix object to use for remapping coordinates
     * @returns {void}
     */
    remapElement(selected: Element, changes: { [key: string]: string }, m: SVGMatrix);

    /**
     * @function module:path.EditorContext#getContainer
     * @returns {Element}
     */
    getContainer(): Element;

    /**
     * @function module:path.EditorContext#getRubberBox
     * @returns {SVGRectElement}
    */
    getRubberBox(): SVGRectElement;

    /**
     * @function module:path.EditorContext#setRubberBox
     * @param {SVGRectElement} rb
     * @returns {SVGRectElement} Same as parameter passed in
     */
    setRubberBox(rb: SVGRectElement): SVGRectElement;

    /**
     * @function module:path.EditorContext#addPtsToSelection
     * @param {PlainObject} cfg
     * @param {boolean} cfg.closedSubpath
     * @param {SVGCircleElement[]} cfg.grips
     * @returns {void}
     */
    addPtsToSelection(cfg: { [key: string]: any, closedSubpath: boolean, grips: SVGCircleElement[] });

    /**
     * @function module:path.EditorContext#endChanges
     * @param {PlainObject} cfg
     * @param {string} cfg.cmd
     * @param {Element} cfg.elem
     * @returns {void}
    */
    endChanges(cfg: { [key: string]: any, cmd: Command, elem: Element });

    /**
     * @function module:path.EditorContext#getDrawnPath
     * @returns {SVGPathElement|null}
     */
    getDrawnPath(): SVGPathElement | null;

    /**
     * @function module:path.EditorContext#setDrawnPath
     * @param {SVGPathElement|null} dp
     * @returns {SVGPathElement|null} The same value as passed in
     */
    setDrawnPath(dp: SVGPathElement | null): SVGPathElement | null;
}

/**
* @function module:path.insertItemBefore
* @param {Element} elem
* @param {Segment} newseg
* @param {Integer} index
* @returns {void}
*/
export const insertItemBefore = insertItemBeforeMethod;

/**
* @function module:path.ptObjToArr
* @todo See if this should just live in `replacePathSeg`
* @param {string} type
* @param {SVGPathSegMovetoAbs|SVGPathSegLinetoAbs|SVGPathSegCurvetoCubicAbs|SVGPathSegCurvetoQuadraticAbs|SVGPathSegArcAbs|SVGPathSegLinetoHorizontalAbs|SVGPathSegLinetoVerticalAbs|SVGPathSegCurvetoCubicSmoothAbs|SVGPathSegCurvetoQuadraticSmoothAbs} segItem
* @returns {ArgumentsArray}
*/
export const ptObjToArr = ptObjToArrMethod;

/**
* @function module:path.getGripPt
* @param {Segment} seg
* @param {module:math.XYObject} altPt
* @returns {module:math.XYObject}
*/
export const getGripPt = getGripPtMethod;

/**
* @function module:path.getPointFromGrip
* @param {module:math.XYObject} pt
* @param {module:path.Path} pth
* @returns {module:math.XYObject}
*/
export const getPointFromGrip = getPointFromGripMethod;

/**
* Requires prior call to `setUiStrings` if `xlink:title`
*    to be set on the grip.
* @function module:path.addPointGrip
* @param {Integer} index
* @param {Integer} x
* @param {Integer} y
* @returns {SVGCircleElement}
*/
export const addPointGrip = addPointGripMethod;

/**
* @function module:path.getGripContainer
* @returns {Element}
*/
export const getGripContainer = getGripContainerMethod;

/**
* Requires prior call to `setUiStrings` if `xlink:title`
*    to be set on the grip.
* @function module:path.addCtrlGrip
* @param {string} id
* @returns {SVGCircleElement}
*/
export const addCtrlGrip = addCtrlGripMethod;

/**
* @function module:path.getCtrlLine
* @param {string} id
* @returns {SVGLineElement}
*/
export const getCtrlLine = getCtrlLineMethod;

/**
* @function module:path.getPointGrip
* @param {Segment} seg
* @param {boolean} update
* @returns {SVGCircleElement}
*/
export const getPointGrip = getPointGripMethod;

/**
* @function module:path.getControlPoints
* @param {Segment} seg
* @returns {PlainObject<string, SVGLineElement|SVGCircleElement>}
*/
export const getControlPoints = getControlPointsMethod;

/**
* This replaces the segment at the given index. Type is given as number.
* @function module:path.replacePathSeg
* @param {Integer} type Possible values set during {@link module:path.init}
* @param {Integer} index
* @param {ArgumentsArray} pts
* @param {SVGPathElement} elem
* @returns {void}
*/
export const replacePathSeg = replacePathSegMethod;

/**
* @function module:path.getSegSelector
* @param {Segment} seg
* @param {boolean} update
* @returns {SVGPathElement}
*/
export const getSegSelector = getSegSelectorMethod;

/**
 * @typedef {PlainObject} Point
 * @property {Integer} x The x value
 * @property {Integer} y The y value
 */

/**
* Takes three points and creates a smoother line based on them.
* @function module:path.smoothControlPoints
* @param {Point | DOMPoint} ct1 - Object with x and y values (first control point)
* @param {Point | DOMPoint} ct2 - Object with x and y values (second control point)
* @param {Point | DOMPoint} pt - Object with x and y values (third point)
* @returns {Point[]} Array of two "smoothed" point objects
*/
export const smoothControlPoints = function (ct1: Point | DOMPoint, ct2: Point | DOMPoint, pt: Point | DOMPoint): [DOMPoint, DOMPoint] | undefined {
    // each point must not be the origin
    const x1 = ct1.x - pt.x;
    const y1 = ct1.y - pt.y;
    const x2 = ct2.x - pt.x;
    const y2 = ct2.y - pt.y;

    if ((x1 !== 0 || y1 !== 0) && (x2 !== 0 || y2 !== 0)) {
        const r1 = Math.sqrt(x1 * x1 + y1 * y1);
        const r2 = Math.sqrt(x2 * x2 + y2 * y2);
        const nct1 = svgCanvas.getSVGRoot().createSVGPoint();
        const nct2 = svgCanvas.getSVGRoot().createSVGPoint();

        let anglea = Math.atan2(y1, x1);
        let angleb = Math.atan2(y2, x2);

        if (anglea < 0) {
            anglea += 2 * Math.PI;
        }
        if (angleb < 0) {
            angleb += 2 * Math.PI;
        }

        const angleBetween = Math.abs(anglea - angleb);
        const angleDiff = Math.abs(Math.PI - angleBetween) / 2;

        let newAnglea, newAngleb;
        if (anglea - angleb > 0) {
            newAnglea = angleBetween < Math.PI ? (anglea + angleDiff) : (anglea - angleDiff);
            newAngleb = angleBetween < Math.PI ? (angleb - angleDiff) : (angleb + angleDiff);
        } else {
            newAnglea = angleBetween < Math.PI ? (anglea - angleDiff) : (anglea + angleDiff);
            newAngleb = angleBetween < Math.PI ? (angleb + angleDiff) : (angleb - angleDiff);
        }

        // rotate the points
        nct1.x = r1 * Math.cos(newAnglea) + pt.x;
        nct1.y = r1 * Math.sin(newAnglea) + pt.y;
        nct2.x = r2 * Math.cos(newAngleb) + pt.x;
        nct2.y = r2 * Math.sin(newAngleb) + pt.y;

        return [nct1, nct2];
    }

    return undefined;
};

/**
* @function module:path.getPath_
* @param {SVGPathElement} elem
* @returns {module:path.Path}
*/
export const getPath_ = function (elem: SVGPathElement): Path {
    let p = pathData[elem.id];
    if (!p) {
        p = pathData[elem.id] = new Path(elem);
    }
    return p;
};

/**
* @function module:path.removePath_
* @param {string} id
* @returns {void}
*/
export const removePath_ = function (id: string) {
    if (id in pathData) {
        delete pathData[id];
    }
};

let newcx, newcy, oldcx, oldcy, angle;

const getRotVals = function (x: Float, y: Float) {
    let dx = x - oldcx;
    let dy = y - oldcy;

    // rotate the point around the old center
    let r = Math.sqrt(dx * dx + dy * dy);
    let theta = Math.atan2(dy, dx) + angle;
    dx = r * Math.cos(theta) + oldcx;
    dy = r * Math.sin(theta) + oldcy;

    // dx,dy should now hold the actual coordinates of each
    // point after being rotated

    // now we want to rotate them around the new center in the reverse direction
    dx -= newcx;
    dy -= newcy;

    r = Math.sqrt(dx * dx + dy * dy);
    theta = Math.atan2(dy, dx) - angle;

    return {
        x: r * Math.cos(theta) + newcx,
        y: r * Math.sin(theta) + newcy
    };
};

// If the path was rotated, we must now pay the piper:
// Every path point must be rotated into the rotated coordinate system of
// its old center, then determine the new center, then rotate it back
// This is because we want the path to remember its rotation

/**
* @function module:path.recalcRotatedPath
* @todo This is still using ye olde transform methods, can probably
* be optimized or even taken care of by `recalculateDimensions`
* @returns {void}
*/
export const recalcRotatedPath = function () {
    const currentPath = path.elem;
    angle = getRotationAngle(currentPath, true);
    if (!angle) {
        return;
    }

    // selectedBBoxes[0] = path.oldbbox;
    const oldbox = path.oldbbox; // selectedBBoxes[0],
    oldcx = oldbox.x + oldbox.width / 2;
    oldcy = oldbox.y + oldbox.height / 2;
    const box = getBBox(currentPath);
    newcx = box.x + box.width / 2;
    newcy = box.y + box.height / 2;

    // un-rotate the new center to the proper position
    const dx = newcx - oldcx,
        dy = newcy - oldcy,
        r = Math.sqrt(dx * dx + dy * dy),
        theta = Math.atan2(dy, dx) + angle;

    newcx = r * Math.cos(theta) + oldcx;
    newcy = r * Math.sin(theta) + oldcy;

    const list = currentPath.pathSegList;

    let i = list.numberOfItems;
    while (i) {
        i -= 1;
        const seg = list.getItem(i) as any; // TODO: Fix as any
        const type = seg.pathSegType;

        if (type === 1) {
            continue;
        }

        const rvals = getRotVals(seg.x, seg.y);
        const points = [rvals.x, rvals.y];

        if (!isNullish(seg.x1) && !isNullish(seg.x2)) {
            const cVals1 = getRotVals(seg.x1, seg.y1);
            const cVals2 = getRotVals(seg.x2, seg.y2);
            points.splice(points.length, 0, cVals1.x, cVals1.y, cVals2.x, cVals2.y);
        }
        replacePathSeg(type, i, points);
    } // loop for each point

  /* box = */ getBBox(currentPath);
    // selectedBBoxes[0].x = box.x; selectedBBoxes[0].y = box.y;
    // selectedBBoxes[0].width = box.width; selectedBBoxes[0].height = box.height;

    // now we must set the new transform to be rotated around the new center
    const Rnc = svgCanvas.getSVGRoot().createSVGTransform(),
        tlist = getTransformList(currentPath);
    Rnc.setRotate((angle * 180.0 / Math.PI), newcx, newcy);
    tlist.replaceItem(Rnc, 0);
};

// ====================================
// Public API starts here

/**
* @function module:path.clearData
* @returns {void}
*/
export const clearData = function () {
    pathData = {};
};

// Making public for mocking
/**
* @function module:path.reorientGrads
* @param {SVGGraphicsElement} elem
* @param {SVGMatrix} m
* @returns {void}
*/
export const reorientGrads = function (elem: SVGGraphicsElement, m: SVGMatrix) {
    const bb = utilsGetBBox(elem);

    for (let i = 0; i < 2; i++) {
        const type = i === 0 ? 'fill' : 'stroke';
        const attrVal = elem.getAttribute(type);

        if (attrVal && attrVal.startsWith('url(')) {
            const grad = getRefElem(attrVal);
            if (grad.tagName === 'linearGradient') {
                let x1 = parseFloat(grad.getAttribute('x1') || "0");
                let y1 = parseFloat(grad.getAttribute('y1') || "0");
                let x2 = parseFloat(grad.getAttribute('x2') || "0");
                let y2 = parseFloat(grad.getAttribute('y2') || "0");

                // Convert to USOU points
                x1 = (bb.width * x1) + bb.x;
                y1 = (bb.height * y1) + bb.y;
                x2 = (bb.width * x2) + bb.x;
                y2 = (bb.height * y2) + bb.y;

                // Transform those points
                const pt1 = transformPoint(x1, y1, m);
                const pt2 = transformPoint(x2, y2, m);

                // Convert back to BB points
                const gCoords = {
                    x1: (pt1.x - bb.x) / bb.width,
                    y1: (pt1.y - bb.y) / bb.height,
                    x2: (pt2.x - bb.x) / bb.width,
                    y2: (pt2.y - bb.y) / bb.height
                };

                const newgrad = grad.cloneNode(true) as SVGLinearGradientElement;
                for (const [key, value] of Object.entries(gCoords)) {
                    newgrad.setAttribute(key, value.toString());
                }

                newgrad.id = svgCanvas.getNextId();
                findDefs().append(newgrad);
                elem.setAttribute(type, 'url(#' + newgrad.id + ')');
            }
        }
    }
};

/**
* This is how we map paths to our preferred relative segment types.
* @name module:path.pathMap
* @type {GenericArray}
*/
const pathMap = [
    0, 'z', 'M', 'm', 'L', 'l', 'C', 'c', 'Q', 'q', 'A', 'a',
    'H', 'h', 'V', 'v', 'S', 's', 'T', 't'
];

/**
 * Convert a path to one with only absolute or relative values.
 * @todo move to pathActions.js
 * @function module:path.convertPath
 * @param {SVGPathElement} pth - the path to convert
 * @param {boolean} [toRel] - true of convert to relative
 * @returns {string}
 */
export const convertPath = function (pth: SVGPathElement, toRel?: boolean): string {
    const { pathSegList } = pth;
    const len = pathSegList.numberOfItems;
    let curx = 0, cury = 0;
    let d = '';
    let lastM = null;

    for (let i = 0; i < len; ++i) {
        const seg = pathSegList.getItem(i) as any; // TODO: Fix as any
        // if these properties are not in the segment, set them to zero
        let x = seg.x || 0,
            y = seg.y || 0,
            x1 = seg.x1 || 0,
            y1 = seg.y1 || 0,
            x2 = seg.x2 || 0,
            y2 = seg.y2 || 0;

        const type = seg.pathSegType;
        let letter = pathMap[type][toRel ? 'toLowerCase' : 'toUpperCase']();

        switch (type) {
            case 1: // z,Z closepath (Z/z)
                d += 'z';
                if (lastM && !toRel) {
                    curx = lastM[0];
                    cury = lastM[1];
                }
                break;
            case 12: // absolute horizontal line (H)
                x -= curx;
            // Fallthrough
            case 13: // relative horizontal line (h)
                if (toRel) {
                    y = 0;
                    curx += x;
                    letter = 'l';
                } else {
                    y = cury;
                    x += curx;
                    curx = x;
                    letter = 'L';
                }
                // Convert to "line" for easier editing
                d += pathDSegment(letter, [[x, y]]);
                break;
            case 14: // absolute vertical line (V)
                y -= cury;
            // Fallthrough
            case 15: // relative vertical line (v)
                if (toRel) {
                    x = 0;
                    cury += y;
                    letter = 'l';
                } else {
                    x = curx;
                    y += cury;
                    cury = y;
                    letter = 'L';
                }
                // Convert to "line" for easier editing
                d += pathDSegment(letter, [[x, y]]);
                break;
            case 2: // absolute move (M)
            case 4: // absolute line (L)
            case 18: // absolute smooth quad (T)
            case 10: // absolute elliptical arc (A)
                x -= curx;
                y -= cury;
            // Fallthrough
            case 5: // relative line (l)
            case 3: // relative move (m)
            case 19: // relative smooth quad (t)
                if (toRel) {
                    curx += x;
                    cury += y;
                } else {
                    x += curx;
                    y += cury;
                    curx = x;
                    cury = y;
                }
                if (type === 2 || type === 3) { lastM = [curx, cury]; }

                d += pathDSegment(letter, [[x, y]]);
                break;
            case 6: // absolute cubic (C)
                x -= curx; x1 -= curx; x2 -= curx;
                y -= cury; y1 -= cury; y2 -= cury;
            // Fallthrough
            case 7: // relative cubic (c)
                if (toRel) {
                    curx += x;
                    cury += y;
                } else {
                    x += curx; x1 += curx; x2 += curx;
                    y += cury; y1 += cury; y2 += cury;
                    curx = x;
                    cury = y;
                }
                d += pathDSegment(letter, [[x1, y1], [x2, y2], [x, y]]);
                break;
            case 8: // absolute quad (Q)
                x -= curx; x1 -= curx;
                y -= cury; y1 -= cury;
            // Fallthrough
            case 9: // relative quad (q)
                if (toRel) {
                    curx += x;
                    cury += y;
                } else {
                    x += curx; x1 += curx;
                    y += cury; y1 += cury;
                    curx = x;
                    cury = y;
                }
                d += pathDSegment(letter, [[x1, y1], [x, y]]);
                break;
            // Fallthrough
            case 11: // relative elliptical arc (a)
                if (toRel) {
                    curx += x;
                    cury += y;
                } else {
                    x += curx;
                    y += cury;
                    curx = x;
                    cury = y;
                }
                d += pathDSegment(letter, [[seg.r1, seg.r2]], [
                    seg.angle,
                    (seg.largeArcFlag ? 1 : 0),
                    (seg.sweepFlag ? 1 : 0)
                ], [x, y]);
                break;
            case 16: // absolute smooth cubic (S)
                x -= curx; x2 -= curx;
                y -= cury; y2 -= cury;
            // Fallthrough
            case 17: // relative smooth cubic (s)
                if (toRel) {
                    curx += x;
                    cury += y;
                } else {
                    x += curx; x2 += curx;
                    y += cury; y2 += cury;
                    curx = x;
                    cury = y;
                }
                d += pathDSegment(letter, [[x2, y2], [x, y]]);
                break;
        } // switch on path segment type
    } // for each segment
    return d;
};

/**
 * TODO: refactor callers in `convertPath` to use `getPathDFromSegments` instead of this function.
 * Legacy code refactored from `svgcanvas.pathActions.convertPath`.
 * @param {string} letter - path segment command (letter in potentially either case from {@link module:path.pathMap}; see [SVGPathSeg#pathSegTypeAsLetter]{@link https://www.w3.org/TR/SVG/single-page.html#paths-__svg__SVGPathSeg__pathSegTypeAsLetter})
 * @param {GenericArray<GenericArray<Integer>>} points - x,y points
 * @param {GenericArray<GenericArray<Integer>>} [morePoints] - x,y points
 * @param {Integer[]} [lastPoint] - x,y point
 * @returns {string}
 */
function pathDSegment(letter: string, points: [Integer, Integer][], morePoints?: [Integer, Integer][], lastPoint?: [Integer, Integer]) {
    points.forEach(function (pnt, i) {
        points[i] = shortFloat(pnt);
    });
    let segment = letter + points.join(' ');
    if (morePoints) {
        segment += ' ' + morePoints.join(' ');
    }
    if (lastPoint) {
        segment += ' ' + shortFloat(lastPoint);
    }
    return segment;
}

/**
* Group: Path edit functions.
* Functions relating to editing path elements.
*/
export const pathActions = pathActionsMethod;


const pathActionsContext = {
    getEditorContext() { return editorContext_; },
    getPathMap() { return pathMap; },
    smoothControlPoints,
    addPointGrip,
    recalcRotatedPath,
    removePath_,
    addCtrlGrip,
    getCtrlLine,
    replacePathSeg,
    insertItemBefore,
    getPointFromGrip,
    getGripPt,
    getPath_,
    reorientGrads,
    setLinkControlPoints,
    getLinkControlPoints
};

export type PathActionsContext = typeof pathActionsContext;

const pathMethodsContext = {
    getEditorContext() { return editorContext_; },
    getSegData() { return segData; },
    getUIStrings() { return uiStrings; },
    getPathObj() { return path; },
    setPathObj(obj) { path = obj; },
    getPathFuncs() { return pathFuncs; },
    getLinkControlPts() { return linkControlPts; }
}

export type PathMethodsContext = typeof pathMethodsContext;

/**
* @function module:path.init
* @param {module:path.EditorContext} editorContext
* @returns {void}
*/
export const init = function (_svgCanvas: SvgCanvas, editorContext: EditorContext) {
    svgCanvas = _svgCanvas;
    editorContext_ = editorContext;

    pathFuncs = [0, 'ClosePath'];
    const pathFuncsStrs = [
        'Moveto',
        'Lineto',
        'CurvetoCubic',
        'CurvetoQuadratic',
        'Arc',
        'LinetoHorizontal',
        'LinetoVertical',
        'CurvetoCubicSmooth',
        'CurvetoQuadraticSmooth'
    ];

    pathActionsInit(svgCanvas, pathActionsContext);
    pathMethodInit(svgCanvas, pathMethodsContext);

    pathFuncsStrs.forEach(function (s) {
        pathFuncs.push(s + 'Abs');
        pathFuncs.push(s + 'Rel');
    });
};

// end pathActions
